import { Injectable } from "@angular/core";
import { KeyValuePair } from "../../../../shared/interfaces/key-value-pair.interface";
import { LorenMeteringType } from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenAutomationActionEnum } from "../../enums/loren-automation-action.enum";
import { LorenAllConditionsEnum } from "../../enums/loren-all-conditions-enum";
import { LorenBinaryConditionEnum } from "../../enums/loren-binary-condition.enum";
import { LorenUtilsService } from "../../../../shared/services/loren-utils-service/loren-utils.service";
import {
  getFillLevelTranslationKey,
  isFillLevelSensor,
} from "../../../asset/fill-level.utils";

@Injectable({
  providedIn: "root",
})
export class LorenAutomationPlanUtilsService {
  private static arcingConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.arcing_higher_than`,
      value: LorenAllConditionsEnum.ARCING_PROBABILITY_HIGHER_THAN,
    },
  ];

  private static ntlConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.ntl_higher_than`,
      value: LorenAllConditionsEnum.NTL_PROBABILITY_HIGHER_THAN,
    },
  ];


  private static humidityConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.humidity_higher_than`,
      value: LorenAllConditionsEnum.HUMIDITY_HIGHER_THAN,
    },
    {
      key: `automation_plans.humidity_lower_than`,
      value: LorenAllConditionsEnum.HUMIDITY_LOWER_THAN,
    },
  ];

  private static fillLevelConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.fill_level_equals`,
      value: LorenAllConditionsEnum.FILL_LEVEL_EQUALS,
    },
  ];

  private static illuminationConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.illumination_higher_than`,
      value: LorenAllConditionsEnum.ILLUMINATION_HIGHER_THAN,
    },
    {
      key: `automation_plans.illumination_lower_than`,
      value: LorenAllConditionsEnum.ILLUMINATION_LOWER_THAN,
    },
  ];

  private static itemCountConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.item_count_higher_than`,
      value: LorenAllConditionsEnum.ITEM_COUNT_HIGHER_THAN,
    },
    {
      key: `automation_plans.item_count_lower_than`,
      value: LorenAllConditionsEnum.ITEM_COUNT_LOWER_THAN,
    },
  ];

  private static externalVoltageConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.external_voltage_higher_than`,
      value: LorenAllConditionsEnum.EXTERNAL_VOLTAGE_HIGHER_THAN,
    },
    {
      key: `automation_plans.external_voltage_lower_than`,
      value: LorenAllConditionsEnum.EXTERNAL_VOLTAGE_LOWER_THAN,
    },
  ];

  private static distanceConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.distance_higher_than`,
      value: LorenAllConditionsEnum.DISTANCE_MORE_THAN,
    },
    {
      key: `automation_plans.distance_lower_than`,
      value: LorenAllConditionsEnum.DISTANCE_LESS_THAN,
    },
  ];

  private static stateConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.state_on`,
      value: LorenAllConditionsEnum.STATE_ON,
    },
    {
      key: `automation_plans.state_off`,
      value: LorenAllConditionsEnum.STATE_OFF,
    },
  ];

  private static doorWindowConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.binary_is_open`,
      value: LorenAllConditionsEnum.BINARY_IS_OPEN,
    },
    {
      key: `automation_plans.binary_is_closed`,
      value: LorenAllConditionsEnum.BINARY_IS_CLOSED,
    },
  ];

  private static ignitionConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.ignition_off`,
      value: LorenAllConditionsEnum.IGNITION_TRUE,
    },
    {
      key: `automation_plans.ignition_on`,
      value: LorenAllConditionsEnum.IGNITION_FALSE,
    },
  ];

  private static waterLeakConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.water_leak_true`,
      value: LorenAllConditionsEnum.WATER_LEAK_TRUE,
    },
    {
      key: `automation_plans.water_leak_false`,
      value: LorenAllConditionsEnum.WATER_LEAK_FALSE,
    },
  ];

  private static smokeConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.smoke_true`,
      value: LorenAllConditionsEnum.SMOKE_TRUE,
    },
    {
      key: `automation_plans.smoke_false`,
      value: LorenAllConditionsEnum.SMOKE_FALSE,
    },
  ];

  private static tiltConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.tilt_true`,
      value: LorenAllConditionsEnum.TILT_TRUE,
    },
    {
      key: `automation_plans.tilt_false`,
      value: LorenAllConditionsEnum.TILT_FALSE,
    },
  ];

  private static lightBinaryConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.light_is_bright`,
      value: LorenAllConditionsEnum.LIGHT_IS_BRIGHT,
    },
    {
      key: `automation_plans.light_is_dark`,
      value: LorenAllConditionsEnum.LIGHT_IS_DARK,
    },
  ];

  private static tempConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.temp_higher_than`,
      value: LorenAllConditionsEnum.TEMPERATURE_HIGHER_THAN,
    },
    {
      key: `automation_plans.temp_lower_than`,
      value: LorenAllConditionsEnum.TEMPERATURE_LOWER_THAN,
    },
  ];

  private static soilTempConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.soil_temp_higher_than`,
      value: LorenAllConditionsEnum.SOIL_TEMPERATURE_HIGHER_THAN,
    },
    {
      key: `automation_plans.soil_temp_lower_than`,
      value: LorenAllConditionsEnum.SOIL_TEMPERATURE_LOWER_THAN,
    },
  ];

  private static soilMoistureConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.soil_moisture_higher_than`,
      value: LorenAllConditionsEnum.SOIL_MOISTURE_HIGHER_THAN,
    },
    {
      key: `automation_plans.soil_moisture_lower_than`,
      value: LorenAllConditionsEnum.SOIL_MOISTURE_LOWER_THAN,
    },
  ];

  private static carbonDioxideConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.carbon_dioxide_higher_than`,
      value: LorenAllConditionsEnum.CARBON_DIOXIDE_HIGHER_THAN,
    },
    {
      key: `automation_plans.carbon_dioxide_lower_than`,
      value: LorenAllConditionsEnum.CARBON_DIOXIDE_LOWER_THAN,
    },
  ];

  private static barometricPressureConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.barometric_pressure_higher_than`,
      value: LorenAllConditionsEnum.BAROMETRIC_PRESSURE_HIGHER_THAN,
    },
    {
      key: `automation_plans.barometric_pressure_lower_than`,
      value: LorenAllConditionsEnum.BAROMETRIC_PRESSURE_LOWER_THAN,
    },
  ];

  private static externalCurrentConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.external_current_higher_than`,
      value: LorenAllConditionsEnum.EXTERNAL_CURRENT_HIGHER_THAN,
    },
    {
      key: `automation_plans.external_current_lower_than`,
      value: LorenAllConditionsEnum.EXTERNAL_CURRENT_LOWER_THAN,
    },
  ];

  private static currentConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.current_higher_than`,
      value: LorenAllConditionsEnum.CURRENT_HIGHER_THAN,
    },
    {
      key: `automation_plans.current_lower_than`,
      value: LorenAllConditionsEnum.CURRENT_LOWER_THAN,
    },
  ];

  private static voltageConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.voltage_higher_than`,
      value: LorenAllConditionsEnum.VOLTAGE_HIGHER_THAN,
    },
    {
      key: `automation_plans.voltage_lower_than`,
      value: LorenAllConditionsEnum.VOLTAGE_LOWER_THAN,
    },
  ];

  private static fluidFlowConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.fluid_flow_higher_than`,
      value: LorenAllConditionsEnum.FLUID_FLOW_HIGHER_THAN,
    },
    {
      key: `automation_plans.fluid_flow_lower_than`,
      value: LorenAllConditionsEnum.FLUID_FLOW_LOWER_THAN,
    },
  ];

  private static lightConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.light_higher_than`,
      value: LorenAllConditionsEnum.LIGHT_HIGHER_THAN,
    },
    {
      key: `automation_plans.light_lower_than`,
      value: LorenAllConditionsEnum.LIGHT_LOWER_THAN,
    },
  ];

  private static hchoConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.hcho_higher_than`,
      value: LorenAllConditionsEnum.HCHO_HIGHER_THAN,
    },
    {
      key: `automation_plans.hcho_lower_than`,
      value: LorenAllConditionsEnum.HCHO_LOWER_THAN,
    },
  ];

  private static ozoneConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.ozone_higher_than`,
      value: LorenAllConditionsEnum.OZONE_HIGHER_THAN,
    },
    {
      key: `automation_plans.ozone_lower_than`,
      value: LorenAllConditionsEnum.OZONE_LOWER_THAN,
    },
  ];

  private static tvocConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.tvoc_higher_than`,
      value: LorenAllConditionsEnum.TVOC_IAQ_HIGHER_THAN,
    },
    {
      key: `automation_plans.tvoc_lower_than`,
      value: LorenAllConditionsEnum.TVOC_IAQ_LOWER_THAN,
    },
  ];

  private static pm2_5Conditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.pm_2_5_higher_than`,
      value: LorenAllConditionsEnum.PM2_5_HIGHER_THAN,
    },
    {
      key: `automation_plans.pm_2_5_lower_than`,
      value: LorenAllConditionsEnum.PM2_5_LOWER_THAN,
    },
  ];

  private static pm10Conditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.pm_10_higher_than`,
      value: LorenAllConditionsEnum.PM10_HIGHER_THAN,
    },
    {
      key: `automation_plans.pm_10_lower_than`,
      value: LorenAllConditionsEnum.PM10_LOWER_THAN,
    },
  ];

  private static geofenceConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.asset_in_area`,
      value: LorenAllConditionsEnum.IN_AREA,
    },
    {
      key: `automation_plans.asset_out_of_area`,
      value: LorenAllConditionsEnum.OUT_OF_AREA,
    },
  ];

  private static movementConditions: KeyValuePair<string>[] = [
    {
      key: `automation_plans.movement_start`,
      value: LorenAllConditionsEnum.MOVEMENT_START,
    },
    {
      key: `automation_plans.movement_stop`,
      value: LorenAllConditionsEnum.MOVEMENT_STOP,
    },
  ];

  static conditionsBasedOnMeteringTypes: Map<
    LorenMeteringType,
    { key: string; value: string }[]
  > = new Map<LorenMeteringType, { key: string; value: string }[]>([
    [
      LorenMeteringType.HUMIDITY_SENSOR,
      LorenAutomationPlanUtilsService.humidityConditions,
    ],
    [
      LorenMeteringType.FILL_LEVEL_SENSOR,
      LorenAutomationPlanUtilsService.fillLevelConditions,
    ],
    [
      LorenMeteringType.TEMPERATURE_SENSOR,
      LorenAutomationPlanUtilsService.tempConditions,
    ],
    [
      LorenMeteringType.GPS_TRACKER_SENSOR,
      LorenAutomationPlanUtilsService.geofenceConditions,
    ],
    [
      LorenMeteringType.SOIL_TEMPERATURE_SENSOR,
      LorenAutomationPlanUtilsService.soilTempConditions,
    ],
    [
      LorenMeteringType.SOIL_MOISTURE_SENSOR,
      LorenAutomationPlanUtilsService.soilMoistureConditions,
    ],
    [
      LorenMeteringType.ILLUMINATION_SENSOR,
      LorenAutomationPlanUtilsService.illuminationConditions,
    ],
    [
      LorenMeteringType.EXTERNAL_VOLTAGE_SENSOR,
      LorenAutomationPlanUtilsService.externalVoltageConditions,
    ],
    [
      LorenMeteringType.DISTANCE_SENSOR,
      LorenAutomationPlanUtilsService.distanceConditions,
    ],
    [
      LorenMeteringType.BINARY_SENSOR,
      LorenAutomationPlanUtilsService.stateConditions,
    ],
    [
      LorenMeteringType.DOOR_WINDOW_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.doorWindowConditions,
    ],
    [
      LorenMeteringType.SMOKE_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.smokeConditions,
    ],
    [
      LorenMeteringType.TILT_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.tiltConditions,
    ],
    [
      LorenMeteringType.IGNITION_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.ignitionConditions,
    ],
    [
      LorenMeteringType.WATER_LEAK_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.waterLeakConditions,
    ],
    [
      LorenMeteringType.CO2_SENSOR,
      LorenAutomationPlanUtilsService.carbonDioxideConditions,
    ],
    [
      LorenMeteringType.BAROMETRIC_PRESSURE_SENSOR,
      LorenAutomationPlanUtilsService.barometricPressureConditions,
    ],
    [
      LorenMeteringType.EXTERNAL_CURRENT_SENSOR,
      LorenAutomationPlanUtilsService.externalCurrentConditions,
    ],
    [
      LorenMeteringType.CURRENT_SENSOR,
      LorenAutomationPlanUtilsService.currentConditions,
    ],
    [
      LorenMeteringType.VOLTAGE_SENSOR,
      LorenAutomationPlanUtilsService.voltageConditions,
    ],
    [
      LorenMeteringType.FLUID_FLOW_SENSOR,
      LorenAutomationPlanUtilsService.fluidFlowConditions,
    ],
    [
      LorenMeteringType.LIGHT_SENSOR,
      LorenAutomationPlanUtilsService.lightConditions,
    ],
    [
      LorenMeteringType.HCHO_SENSOR,
      LorenAutomationPlanUtilsService.hchoConditions,
    ],
    [
      LorenMeteringType.OZONE_SENSOR,
      LorenAutomationPlanUtilsService.ozoneConditions,
    ],
    [
      LorenMeteringType.TVOC_SENSOR,
      LorenAutomationPlanUtilsService.tvocConditions,
    ],
    [
      LorenMeteringType.PM2_5_SENSOR,
      LorenAutomationPlanUtilsService.pm2_5Conditions,
    ],
    [
      LorenMeteringType.PM10_SENSOR,
      LorenAutomationPlanUtilsService.pm10Conditions,
    ],
    [
      LorenMeteringType.MOVEMENT_SENSOR,
      LorenAutomationPlanUtilsService.movementConditions,
    ],
    [
      LorenMeteringType.PIR_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.movementConditions,
    ],
    [
      LorenMeteringType.LIGHT_BINARY_SENSOR,
      LorenAutomationPlanUtilsService.lightBinaryConditions,
    ],
    [
      LorenMeteringType.NTL_SENSOR,
      LorenAutomationPlanUtilsService.ntlConditions
    ],
    [
      LorenMeteringType.ARCING_SENSOR,
      LorenAutomationPlanUtilsService.arcingConditions
    ],
    [
      LorenMeteringType.SOIL_MOISTURE_SENSOR_VWC,
      LorenAutomationPlanUtilsService.soilMoistureConditions
    ],
    [
      LorenMeteringType.ITEM_COUNT_SENSOR,
      LorenAutomationPlanUtilsService.itemCountConditions,
    ],
  ]);

  static binaryConditionsValues = new Map<LorenAllConditionsEnum, boolean>(
    [
      [LorenAllConditionsEnum.STATE_OFF, false],
      [LorenAllConditionsEnum.STATE_ON, true],
      [LorenAllConditionsEnum.BINARY_IS_CLOSED, true],
      [LorenAllConditionsEnum.IGNITION_FALSE, false],
      [LorenAllConditionsEnum.WATER_LEAK_FALSE, false],
      [LorenAllConditionsEnum.WATER_LEAK_TRUE, true],
      [LorenAllConditionsEnum.SMOKE_FALSE, false],
      [LorenAllConditionsEnum.TILT_FALSE, false],
      [LorenAllConditionsEnum.LIGHT_IS_DARK, false],
      [LorenAllConditionsEnum.MOVEMENT_STOP, false],
      [LorenAllConditionsEnum.BINARY_IS_OPEN, false],
      [LorenAllConditionsEnum.IGNITION_TRUE, true],
      [LorenAllConditionsEnum.SMOKE_TRUE, true],
      [LorenAllConditionsEnum.TILT_TRUE, true],
      [LorenAllConditionsEnum.LIGHT_IS_BRIGHT, true],
      [LorenAllConditionsEnum.MOVEMENT_START, true],
    ]
  );

  static binaryValuesConditions = new Map<
    LorenMeteringType,
    LorenAllConditionsEnum[]
  >([
    [
      LorenMeteringType.BINARY_SENSOR,
      [
        LorenAllConditionsEnum.STATE_OFF,
        LorenAllConditionsEnum.STATE_ON,
      ],
    ],
    [
      LorenMeteringType.DOOR_WINDOW_BINARY_SENSOR,
      [
        LorenAllConditionsEnum.BINARY_IS_OPEN,
        LorenAllConditionsEnum.BINARY_IS_CLOSED,
      ],
    ],
    [
      LorenMeteringType.IGNITION_BINARY_SENSOR,
      [
        LorenAllConditionsEnum.IGNITION_FALSE,
        LorenAllConditionsEnum.IGNITION_TRUE,
      ],
    ],
    [
      LorenMeteringType.WATER_LEAK_BINARY_SENSOR,
      [
        LorenAllConditionsEnum.WATER_LEAK_FALSE,
        LorenAllConditionsEnum.WATER_LEAK_TRUE,
      ],
    ],
    [
      LorenMeteringType.LIGHT_BINARY_SENSOR,
      [
        LorenAllConditionsEnum.LIGHT_IS_DARK,
        LorenAllConditionsEnum.LIGHT_IS_BRIGHT,
      ],
    ],
    [
      LorenMeteringType.MOVEMENT_SENSOR,
      [
        LorenAllConditionsEnum.MOVEMENT_STOP,
        LorenAllConditionsEnum.MOVEMENT_START,
      ],
    ],
    [
      LorenMeteringType.PIR_BINARY_SENSOR,
      [
        LorenAllConditionsEnum.MOVEMENT_STOP,
        LorenAllConditionsEnum.MOVEMENT_START,
      ],
    ],
    [
      LorenMeteringType.SMOKE_BINARY_SENSOR,
      [LorenAllConditionsEnum.SMOKE_FALSE, LorenAllConditionsEnum.SMOKE_TRUE],
    ],
    [
      LorenMeteringType.TILT_BINARY_SENSOR,
      [LorenAllConditionsEnum.TILT_FALSE, LorenAllConditionsEnum.TILT_TRUE],
    ],
  ]);

  static getActionDescription(action: string): string {
    if (action === LorenAutomationActionEnum.SEND_USER_PUSH_NOTIFICATION) {
      return `automation_plans.send_user_push_notification`;
    } else if (action === LorenAutomationActionEnum.SEND_USER_EMAIL) {
      return `automation_plans.send_user_email`;
    } else if (action === LorenAutomationActionEnum.SEND_USER_SMS) {
      return `automation_plans.send_sms_notification`;
    } else if (action === LorenAutomationActionEnum.SET_RELAY_ON) {
      return `automation_plans.set_relay_on`;
    } else if (action === LorenAutomationActionEnum.SET_RELAY_OFF) {
      return `automation_plans.set_relay_off`;
    }
    return null;
  }

  static getConditionText(c, assets, geozones, transloco) {
    const { condition, meteringId, areaId, value, warningValue } = c;
    const cIsBinary =
      !condition || Object.values(LorenBinaryConditionEnum).includes(condition);
    if (assets && (geozones || !areaId)) {
      const geofenceZone = LorenUtilsService.getGeozoneFromId(
        geozones,
        areaId
      )?.name;
      const asset = LorenUtilsService.getAssetFromMeteringId(
        assets,
        meteringId
      );
      if (!asset) return 0;
      const metering = LorenUtilsService.getMeteringFromId(
        asset?.metering,
        meteringId
      );
      const conditionFull = cIsBinary
        ? LorenAutomationPlanUtilsService.binaryValuesConditions.get(
            metering?.type
          )?.[value ? 1 : 0]
        : condition;

      const conditionTranslation = transloco.translate(
        LorenAutomationPlanUtilsService.conditionsBasedOnMeteringTypes
          .get(metering?.type)
          .find((i) => i.value === conditionFull).key
      );
      const displayValue = isFillLevelSensor(metering?.type)
        ? (getFillLevelTranslationKey(value)
            ? transloco.translate(getFillLevelTranslationKey(value))
            : value)
        : value;
      const displayWarningValue = isFillLevelSensor(metering?.type)
        ? (getFillLevelTranslationKey(warningValue)
            ? transloco.translate(getFillLevelTranslationKey(warningValue))
            : warningValue)
        : warningValue;
      return `${transloco.translate("automation_plans.if")} ${asset?.name} (${
        metering?.description
      }) ${conditionTranslation} ${cIsBinary ? "" : ((geofenceZone || displayValue) ?? '-')} ${
        warningValue !== null && warningValue !== undefined && warningValue !== ""
          ? `(${transloco.translate(
              "automation_plans_form.warning_value"
            )}: ${displayWarningValue})`
          : ""
      }`;
    }
  }
}
